import { forwardRef, useEffect, useRef, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { Input } from '../Input';

const libraries = ['places'];

const parseAddressComponents = (components) => {
  if (!Array.isArray(components)) return {};

  const find = (type) => components.find((c) => c.types.includes(type));

  const streetNumber = find('street_number')?.long_name || '';
  const route = find('route')?.long_name || '';
  const city =
    find('locality')?.long_name ||
    find('sublocality_level_1')?.long_name ||
    find('administrative_area_level_3')?.long_name ||
    '';
  const county = find('administrative_area_level_2')?.long_name || '';
  const stateComponent = find('administrative_area_level_1');
  const postalCode = find('postal_code')?.long_name || '';

  return {
    street: [streetNumber, route].filter(Boolean).join(' ').trim(),
    city,
    county,
    stateName: stateComponent?.long_name || '',
    stateCode: stateComponent?.short_name || '',
    postalCode,
  };
};

const fetchPlaceDetails = (placeId, anchor) =>
  new Promise((resolve, reject) => {
    const service = new window.google.maps.places.PlacesService(anchor);
    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address', 'geometry'],
      },
      (result, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          result
        ) {
          resolve(result);
        } else {
          reject(status);
        }
      }
    );
  });

const reverseGeocode = (location) =>
  new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location }, (results, status) => {
      if (status === 'OK' && results?.[0]) resolve(results[0]);
      else reject(status);
    });
  });

const GooglePlacesAddressInput = forwardRef(
  (
    {
      value,
      placeholder,
      disabled,
      onChange,
      onAddressSelected,
      onFocus,
      onBlur,
      isError,
      errorMessage,
      helperText,
      name,
      showTypingHint = false,
      hideFallbackHelperText = false,
    },
    ref
  ) => {
    const internalRef = useRef(null);
    const inputRef = ref || internalRef;
    const autocompleteRef = useRef(null);
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
    const [scriptHasAttempted, setScriptHasAttempted] = useState(false);

    // Latest-callback refs: keep the place_changed handler stable so the
    // Autocomplete instance is created once instead of being torn down and
    // rebuilt on every parent render (which caused the "first click doesn't
    // populate fields" bug — the freshly-recreated instance had no place
    // details cached yet, so getPlace() returned an object missing
    // address_components and the listener returned early).
    const onChangeRef = useRef(onChange);
    const onAddressSelectedRef = useRef(onAddressSelected);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
      onAddressSelectedRef.current = onAddressSelected;
    }, [onAddressSelected]);

    const shouldLoadScript = Boolean(apiKey);

    const { isLoaded, loadError } = useLoadScript({
      googleMapsApiKey: apiKey || '',
      libraries,
      // Don't set preventGoogleFontsLoading: it swaps document.head.insertBefore/
      // appendChild for versions that drop any <style> inserted empty, which is
      // how emotion (react-select) adds rules in dev, so dropdowns lose styling.
      language: 'en',
      id: 'google-places-script',
      onLoad: () => setScriptHasAttempted(true),
      onError: () => setScriptHasAttempted(true),
    });

    useEffect(() => {
      if (!shouldLoadScript) setScriptHasAttempted(true);
    }, [shouldLoadScript]);

    useEffect(() => {
      if (!isLoaded || loadError || !shouldLoadScript) return undefined;
      if (!inputRef.current || autocompleteRef.current) return undefined;

      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: ['us'] },
          fields: [
            'address_components',
            'formatted_address',
            'geometry',
            'place_id',
          ],
        }
      );
      autocompleteRef.current = autocomplete;

      const handlePlace = async () => {
        const place = autocomplete.getPlace();
        if (!place) return;

        let components = place.address_components;
        let formattedAddress = place.formatted_address;
        let geometry = place.geometry;

        // Fallback: when the user clicks fast, getPlace() can return an object
        // without address_components even though place_id is present. Refetch
        // explicitly so we don't bail silently like the old code did.
        if (!components && place.place_id && inputRef.current) {
          try {
            const detailed = await fetchPlaceDetails(
              place.place_id,
              inputRef.current
            );
            components = detailed.address_components;
            formattedAddress = formattedAddress || detailed.formatted_address;
            geometry = geometry || detailed.geometry;
          } catch {
            return;
          }
        }
        if (!components) return;

        let parsed = parseAddressComponents(components);
        const finalAddress =
          formattedAddress ||
          parsed.street ||
          inputRef.current?.value ||
          '';

        // Reverse-geocode if postal code is missing — common for
        // establishments and landmarks.
        if (!parsed.postalCode && geometry?.location) {
          try {
            const fallback = await reverseGeocode(geometry.location);
            const fallbackParsed = parseAddressComponents(
              fallback.address_components
            );
            parsed = {
              ...parsed,
              postalCode: fallbackParsed.postalCode || parsed.postalCode,
              county: parsed.county || fallbackParsed.county,
            };
          } catch {
            // Use what we have.
          }
        }

        // Emit visible-value update + parsed details together so React batches
        // them into a single re-render. Order: onChange first (sets the long
        // formatted string), then onAddressSelected (sets street to just the
        // line-1 portion plus city/state/zip/county/stateCode).
        onChangeRef.current?.(finalAddress);
        onAddressSelectedRef.current?.({
          formattedAddress: finalAddress,
          ...parsed,
        });
      };

      const listener = autocomplete.addListener('place_changed', handlePlace);

      return () => {
        if (listener) window.google.maps.event.removeListener(listener);
        if (autocompleteRef.current && window.google?.maps?.event) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );
        }
        autocompleteRef.current = null;
      };
    }, [isLoaded, loadError, shouldLoadScript, inputRef]);

    const handleChange = (event) => {
      onChange?.(event.target.value);
    };

    let derivedHelperText;
    if (!isError) {
      if (helperText) {
        derivedHelperText = helperText;
      } else if (
        !hideFallbackHelperText &&
        !shouldLoadScript &&
        scriptHasAttempted
      ) {
        derivedHelperText = 'Google Places not available. Enter address manually.';
      } else if (showTypingHint) {
        derivedHelperText = 'Start typing to search address';
      }
    }

    return (
      <Input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        error={isError ? errorMessage : undefined}
        helperText={derivedHelperText}
        name={name}
      />
    );
  }
);

GooglePlacesAddressInput.displayName = 'GooglePlacesAddressInput';

export default GooglePlacesAddressInput;
